// The coldroom OVERFLOW section — what is standing in this room but on no cell.
//
// It exists because the map is smaller than the floor. When every grid tile is
// taken the extra pallets still go in the room, and before mig 20261021100001
// there was nowhere to record them: the placement picker's last word was "No
// available cells for this pallet." So the fruit went in the aisle and stayed
// off the register, where it ages invisibly, misses the FIFO review and accrues
// no cold-storage cost. This section is the register for that fruit.
//
// TWO LISTS, NOT ONE, and the difference is the point:
//
//   Overflow  coldroom_overflow = TRUE. Somebody deliberately put this here.
//             Amber, expected, not a fault. The room is over its grid, which is
//             a real and legitimate state of a busy packhouse.
//   No cell   in the room with nothing recorded about where. This state was
//             reachable long before the overflow existed (an undispatch that
//             dropped a cell, an old import) and it is a GAP, not a choice.
//             Merging the two would bury a fault inside a feature — see the
//             note at the top of src/lib/coldroomPlacement.ts.
//
// Every row carries its age band, because the reason to look at this list at
// all is that off-grid stock is the easiest stock in the building to forget.
// And every row carries a way OUT: an overflow you cannot leave is a trap, and
// the fruit that ends up here is exactly the fruit that most needs moving when
// a cell frees up.
//
// NOT gated behind a permission. The move buttons here are the same act as the
// "Move →" the cell drawer on this page already offers ungated, and inventing a
// key for one of the two would be a gate in name only — RLS is the boundary
// either way, and an off-grid pallet nobody may move is the trap this section
// exists to remove.

import { AlertTriangle, Layers, Package, Snowflake } from 'lucide-react'
import PalletIdText from '@/components/PalletIdText'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { getAgeStatus, effectiveAgeStart, isExportPallet } from '@/lib/aging'
import type { ProductionFlow } from '@/lib/useProductionFlow'
import { OVERFLOW_LABEL } from '@/lib/coldroomPlacement'
import { fmtDateZA } from '@/lib/dates'

// Not exported: the page passes its own ColdroomPallet / ColdroomBin rows,
// which satisfy these structurally. An export with no importer is a promise of
// a shared contract that does not exist.
interface OverflowPallet {
  pallet_id:     string
  // isExportPallet reads status as part of AgingPallet — every row reaching
  // this section is IN_COLDROOM by the page's own filter, but the field is
  // carried rather than assumed so the type stays the shape the helper wants.
  status:        string
  pallet_type:   string | null
  spec:          string | null
  packed_at:     string
  stocked_at:    string | null
  carton_count:  number | null
  net_weight_kg: number | null
  grower:        string | null
  variety:       string | null
}

interface OverflowBin {
  bin_id:        string
  bin_number:    string
  born_date:     string
  net_weight_kg: number
  grower:        string
  variety:       string
}

interface Props {
  roomCode:        string
  /** Deliberately placed off the grid. */
  pallets:         OverflowPallet[]
  bins:            OverflowBin[]
  /** In the room, no cell recorded, nobody said why. The fault list. */
  strandedPallets: OverflowPallet[]
  strandedBins:    OverflowBin[]
  flow:            ProductionFlow
  /** Open the "put this on a cell" picker for one pallet. */
  onPlacePallet:   (palletId: string) => void
  /** Hand these bins to the page's existing bin-move modal. */
  onMoveBins:      (binIds: string[]) => void
  /** May this viewer open a bin's page (Bin Inventory)? When not, a bin row is
   *  plain text: a link would send a loader to Home with no message (F05 UF-05). */
  canOpenBins:     boolean
}

export default function ColdroomOverflowSection({
  roomCode, pallets, bins, strandedPallets, strandedBins, flow,
  onPlacePallet, onMoveBins, canOpenBins,
}: Props) {
  const overflowCount = pallets.length + bins.length
  const strandedCount = strandedPallets.length + strandedBins.length
  // Nothing off the grid is the ordinary state of a room. No card, no empty
  // state, no reassuring "0 in overflow" — the section appears when there is
  // something to say and is silent otherwise.
  if (overflowCount === 0 && strandedCount === 0) return null

  const list = (ps: OverflowPallet[], bs: OverflowBin[], moveLabel: string) => (
    <OffGridList
      pallets={ps} bins={bs} flow={flow}
      onPlacePallet={onPlacePallet} onMoveBins={onMoveBins} moveLabel={moveLabel}
      canOpenBins={canOpenBins}
    />
  )

  return (
    <>
      {overflowCount > 0 && (
        <OffGridCard
          tone="overflow"
          icon={<Layers size={13} />}
          heading={`${OVERFLOW_LABEL} — ${overflowCount} in ${roomCode}, not on the grid`}
          blurb="In the room, on no cell. They age exactly like the stock on the map above and are counted in the bands at the top of this page — move them onto a cell as soon as one frees up."
          footer={bins.length > 1 ? (
            <button
              onClick={() => onMoveBins(bins.map(b => b.bin_id))}
              className="mt-2 w-full rounded-lg min-h-[44px] text-caption font-semibold bg-amber-800/60 hover:bg-amber-700/60 light:hover:bg-amber-800/80 text-amber-100">
              Move all {bins.length} bins onto the grid →
            </button>
          ) : null}>
          {list(pallets, bins, 'Place on grid')}
        </OffGridCard>
      )}

      {strandedCount > 0 && (
        <OffGridCard
          tone="stranded"
          icon={<AlertTriangle size={12} />}
          heading={`${strandedCount} in ${roomCode} with no cell recorded`}
          /* Worded as a question about the record, not an accusation about the
             floor: the app genuinely does not know where these are, and the
             operator is the only one who can say. */
          blurb={`These are in the room but nothing says where — and nobody marked them as overflow either, so this is a gap in the record rather than a placement. Put each one on its cell, or into the ${OVERFLOW_LABEL.toLowerCase()} if that is where it actually is.`}>
          {list(strandedPallets, strandedBins, 'Record where')}
        </OffGridCard>
      )}
    </>
  )
}

// The card shell both lists sit in. Two tones, because the two lists are two
// different claims: amber-on-amber for a deliberate placement, plain amber for
// a gap in the record.
function OffGridCard({ tone, icon, heading, blurb, footer, children }: {
  tone:     'overflow' | 'stranded'
  icon:     React.ReactNode
  heading:  string
  blurb:    string
  footer?:  React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className={clsx(
      'card p-3 border border-warning-edge',
      tone === 'overflow' && 'bg-amber-950/20',
    )}>
      <p className="text-caption font-semibold text-warning flex items-center gap-1.5">
        {icon} {heading}
      </p>
      <p className="text-caption text-fg-muted mt-1 mb-2">{blurb}</p>
      {children}
      {footer}
    </section>
  )
}

// One list, oldest first — the same FIFO discipline as the register, and the
// ordering that puts the pallet you should move next at the top. Pure and
// module-level: the two lists differ only in what they contain, so building a
// row is not part of rendering one.
interface OffGridRow {
  key:   string
  kind:  'Pallet' | 'Bin'
  /** Null = the viewer can't open this record; the row renders as text. */
  to:    string | null
  ref:   string
  age:   ReturnType<typeof getAgeStatus>
  since: string
  sub:   string
  meta:  string
  move:  () => void
}

const joinParts = (parts: (string | null)[]): string => parts.filter(Boolean).join(' · ')

// Cartons and weight, each omitted when absent rather than rendered as 0 — an
// unweighed pallet has no weight, which is not the same as weighing nothing.
function palletMeta(p: OverflowPallet): string {
  return joinParts([
    p.carton_count  != null ? `${p.carton_count} ctn` : null,
    p.net_weight_kg != null ? `${Math.round(Number(p.net_weight_kg))} kg` : null,
  ]) || '—'
}

function offGridRows(
  pallets:       OverflowPallet[],
  bins:          OverflowBin[],
  flow:          ProductionFlow,
  onPlacePallet: (palletId: string) => void,
  onMoveBins:    (binIds: string[]) => void,
  canOpenBins:   boolean,
): OffGridRow[] {
  const palletRows: OffGridRow[] = pallets.map(p => ({
    key:   `p-${p.pallet_id}`,
    kind:  'Pallet',
    to:    `/pallets/${p.pallet_id}`,
    ref:   p.pallet_id,
    age:   getAgeStatus(effectiveAgeStart(p), isExportPallet(p), true, flow),
    since: effectiveAgeStart(p),
    sub:   joinParts([p.grower, p.variety]) || '—',
    meta:  palletMeta(p),
    move:  () => onPlacePallet(p.pallet_id),
  }))
  const binRows: OffGridRow[] = bins.map(b => ({
    key:   `b-${b.bin_id}`,
    kind:  'Bin',
    to:    canOpenBins ? `/bins/${b.bin_id}` : null,
    ref:   b.bin_number,
    // Bins age from born_date — the original processing date, preserved
    // through rebins, so a rebin cannot buy a freshness illusion.
    age:   getAgeStatus(b.born_date, false, true, flow),
    since: b.born_date,
    sub:   joinParts([b.grower, b.variety]) || '—',
    meta:  `${Math.round(Number(b.net_weight_kg))} kg net`,
    move:  () => onMoveBins([b.bin_id]),
  }))
  return [...palletRows, ...binRows].sort((a, b) => a.since.localeCompare(b.since))
}

// The row opens its record when the viewer may open it, and is the same card
// as plain text when not — never a link the route gate answers with Home.
function RowShell({ to, children }: { to: string | null; children: React.ReactNode }) {
  return to
    ? <Link to={to} className="flex-1 min-w-0 block surface hover:bg-raised rounded-lg p-2.5">{children}</Link>
    : <div className="flex-1 min-w-0 surface rounded-lg p-2.5">{children}</div>
}

function OffGridList({ pallets, bins, flow, onPlacePallet, onMoveBins, moveLabel, canOpenBins }: {
  pallets:       OverflowPallet[]
  bins:          OverflowBin[]
  flow:          ProductionFlow
  onPlacePallet: (palletId: string) => void
  onMoveBins:    (binIds: string[]) => void
  moveLabel:     string
  canOpenBins:   boolean
}) {
  const rows = offGridRows(pallets, bins, flow, onPlacePallet, onMoveBins, canOpenBins)

  return (
    <div className="space-y-1.5">
      {rows.map(r => (
        <div key={r.key} className="flex items-stretch gap-1.5">
          <RowShell to={r.to}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className={clsx('text-caption flex items-center min-w-0', r.kind === 'Bin' ? 'text-cyan-300' : 'text-fg')}>
                  <span className={clsx(
                    'shrink-0 text-caption uppercase rounded px-1 py-0.5 mr-1.5',
                    r.kind === 'Bin' ? 'bg-cyan-900/50 text-cyan-200' : 'surface-raised text-fg-secondary',
                  )}>
                    {r.kind}
                  </span>
                  <PalletIdText id={r.ref} className="min-w-0" />
                </p>
                <p className="text-caption text-fg-secondary truncate flex items-center gap-1.5">
                  {r.kind === 'Bin' ? <Snowflake size={10} /> : <Package size={10} />}
                  {r.meta}
                </p>
                <p className="text-caption text-muted truncate">
                  {r.sub} · since {fmtDateZA(r.since)}
                </p>
              </div>
              <span className={clsx('shrink-0 text-caption rounded-full px-2 py-0.5 font-semibold', r.age.badgeClass)}>
                {r.age.label}
              </span>
            </div>
          </RowShell>
          <button
            onClick={r.move}
            className="shrink-0 w-24 min-h-[44px] rounded-lg text-caption font-semibold surface-raised hover:bg-raised-hover text-fg px-2">
            {moveLabel}
          </button>
        </div>
      ))}
    </div>
  )
}
